/** Composition root: the shell does not import feature modules; only this layer wires them. */
import type { ShellPorts } from './ShellPorts.ts';
import type { AuthApplicationPort } from '@src/modules/auth/domain/ports/AuthApplicationPort.ts';
import type { ShellNavigationPort } from '@src/modules/shell-routing/domain/ports/ShellNavigationPort.ts';
import type { WebComponentsPresentationShellBase } from '@src/modules/shell-routing/infrastructure/drivers/routes/shellDomMount.ts';
import { SHELL_LOGIN_PATH, SHELL_MAIN_PAGE_MANIFEST } from '@src/modules/shell-routing/domain/shellFeatureManifest.ts';

export type ShellRouteHandler = () => void | Promise<void>;

export type ShellRouteBinding = {
  path: string;
  handler: ShellRouteHandler;
};

export function requireAuth(
  auth: AuthApplicationPort,
  navigation: ShellNavigationPort,
  handler: ShellRouteHandler,
): () => Promise<void> {
  return async () => {
    if (!(await auth.isAuthenticated())) {
      navigation.navigate('/login');
      return;
    }
    await handler();
  };
}

function authenticatedPage(
  pageId: string,
  auth: AuthApplicationPort,
  navigation: ShellNavigationPort,
  presentation: WebComponentsPresentationShellBase,
  shellPorts: ShellPorts,
): () => Promise<void> {
  return requireAuth(auth, navigation, presentation.createMainPageHandler(pageId, shellPorts));
}

export function buildShellRouteBindings(
  auth: AuthApplicationPort,
  navigation: ShellNavigationPort,
  presentation: WebComponentsPresentationShellBase,
  shellPorts: ShellPorts,
): ShellRouteBinding[] {
  const page = (pageId: string) => authenticatedPage(pageId, auth, navigation, presentation, shellPorts);

  return [
    { path: SHELL_LOGIN_PATH, handler: () => presentation.mountLoginInApp(shellPorts) },
    ...SHELL_MAIN_PAGE_MANIFEST.map(({ path, pageId }) => ({ path, handler: page(pageId) })),
  ];
}
